/**
 * Printed tow/inventory report and Uniform Traffic Ticket, laid out like the paper forms; and the tow report's
 * damage diagram (tap the spots on the car), used on the screen and on paper.
 */
import { TICKET_VEHICLE_TYPES, TOW_DAMAGE_SPOTS, TOW_REASON_LABEL, type TowReason } from '@civilos/domain';
import { api } from '../shared/api.ts';
import { ErrorBox, Spinner, useLoad } from '../shared/ui.tsx';
import { Box, Check, fmtD, fmtT, Head, Line, PrintSheet, usd } from './printsheet.tsx';

/** Where each numbered spot sits around the car outline (viewBox 0 0 200 260). */
const SPOT_XY: Record<number, [number, number]> = { 1: [100, 14], 2: [176, 60], 3: [176, 130], 4: [176, 200], 5: [100, 246], 6: [24, 200], 7: [24, 130], 8: [24, 60], 9: [100, 62], 10: [100, 130], 11: [100, 202] };

export function DamageDiagram({ spots, onToggle, size = 200 }: { spots: number[]; onToggle?: (n: number) => void; size?: number }) {
  const on = new Set(spots);
  return (
    <svg viewBox="0 -14 200 274" style={{ width: size, height: (size * 274) / 200 }} className="dmg" role={onToggle ? 'group' : 'img'} aria-label="Vehicle body damage">
      <text x="100" y="-5" textAnchor="middle" fontSize="8" fontWeight="700">
        FRONT
      </text>
      <rect x="62" y="34" width="76" height="192" rx="26" fill="none" stroke="#000" strokeWidth="2" />
      <rect x="72" y="48" width="56" height="34" rx="6" fill="none" stroke="#000" strokeWidth="1.4" />
      <path d="M70 92 h60 l-6 14 h-48z" fill="none" stroke="#000" strokeWidth="1.4" />
      <path d="M70 172 h60 l-6 -12 h-48z" fill="none" stroke="#000" strokeWidth="1.4" />
      <rect x="74" y="182" width="52" height="32" rx="5" fill="none" stroke="#000" strokeWidth="1.4" />
      <line x1="64" y1="116" x2="64" y2="152" stroke="#000" strokeWidth="3" />
      <line x1="136" y1="116" x2="136" y2="152" stroke="#000" strokeWidth="3" />
      {TOW_DAMAGE_SPOTS.map((s) => {
        const [x, y] = SPOT_XY[s.n]!;
        const isOn = on.has(s.n);
        return (
          <g key={s.n} className={onToggle ? 'dmg-spot' : undefined} onClick={onToggle ? () => onToggle(s.n) : undefined} role={onToggle ? 'checkbox' : undefined} aria-checked={onToggle ? isOn : undefined} aria-label={`${s.n} ${s.label}`} tabIndex={onToggle ? 0 : undefined} onKeyDown={onToggle ? (e) => (e.key === ' ' || e.key === 'Enter') && (e.preventDefault(), onToggle(s.n)) : undefined}>
            <rect x={x - 9} y={y - 9} width="18" height="18" fill={isOn ? '#000' : '#fff'} stroke="#000" strokeWidth="1.5" />
            {isOn ? (
              <path d={`M${x - 5} ${y - 5} L${x + 5} ${y + 5} M${x + 5} ${y - 5} L${x - 5} ${y + 5}`} stroke="#fff" strokeWidth="2" />
            ) : null}
            <text x={x + (x < 60 ? -14 : x > 140 ? 14 : 0)} y={x < 60 || x > 140 ? y + 3 : y + 20} textAnchor="middle" fontSize="8">
              {s.n}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

const yn = (v: boolean | null | undefined) => (
  <>
    <Check on={v === true}>YES</Check> <Check on={v === false}>NO</Check>
  </>
);

export function TowPrint({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/tows/${id}`), [id]);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { tow: t, agency, officer, driverSignature, events } = r.data;
  const v = t.vehicle ?? {};
  const o = t.owner ?? {};
  const op = t.operator?.sameAsOwner ? { ...o, ...t.operator } : t.operator ?? {};
  const x = t.extra ?? {};
  const holdOn = t.hold && !t.hold_cleared_at;
  const notifiedHow = t.owner_notified_how ?? (events ?? []).find((e: any) => e.action === 'owner_notified')?.note;
  return (
    <PrintSheet label={`Tow report ${t.number}`}>
      <Head
        agency={agency}
        title="Vehicle Tow / Inventory Report"
        right={
          <div className="pf-grid c2 pf-headbox">
            <Box l="Case number" v={t.case_number} mono />
            <Box l="Tow #" v={t.number} mono />
          </div>
        }
      />
      <div className="pf-tow-top">
        <div className={`pf-hold${holdOn ? ' on' : ''}`}>
          <span className="pf-cb big">{holdOn ? '✕' : ''}</span> HOLD
        </div>
        <Box l="Contact for release" v={x.releaseContact} className="grow" />
        <div className="pf-checks">
          <Check on={!holdOn}>CAN BE RELEASED</Check>
          <Check on={holdOn}>CANNOT BE RELEASED</Check>
        </div>
      </div>
      <section className="pf-sideblock">
        <div className="pf-vlabel">Vehicle information</div>
        <div className="pf-grid c8">
          <Box l="Year" v={v.year} />
          <Box l="Make" v={v.make} />
          <Box l="Model" v={v.model} />
          <Box l="Style" v={v.style} />
          <Box l="Color" v={v.color} />
          <Box l="License no." v={v.plate} mono />
          <Box l="State" v={v.plateState} />
          <Box l="Year" v={v.regYear} />
          <Box l="VIN no." v={v.vin} span={8} mono />
          <Box l="Vehicle operator: last" v={op.last ?? (op.name && !op.last ? op.name : '')} span={2} />
          <Box l="First" v={op.first} />
          <Box l="Middle" v={op.middle} />
          <Box l="Sex" v={op.sex} />
          <Box l="Race" v={op.race} />
          <Box l="Date of birth" v={fmtD(op.dob)} span={2} />
          <Box l="Operator’s license no." v={op.dl} span={2} mono />
          <Box l="State" v={op.dlState} />
          <Box l="Address of operator" v={op.address} span={3} />
          <Box l="Telephone no." v={op.phone} span={2} />
          <Box l="Registered owner: last" v={o.last ?? (o.name && !o.last ? o.name : '')} span={2} />
          <Box l="First" v={o.first} />
          <Box l="Middle" v={o.middle} />
          <Box l="Address of registered owner" v={o.address} span={2} />
          <Box l="Telephone no." v={o.phone} span={2} />
          <Box l="Was owner notified of towed vehicle location?" v={yn(t.owner_notified_at ? true : x.ownerNotNotifiedReason ? false : null)} span={3} />
          <Box l="If no, reason" v={x.ownerNotNotifiedReason} span={5} />
          <Box l="If notified, how?" v={notifiedHow ? `${notifiedHow} (${fmtD(t.owner_notified_at)} ${fmtT(t.owner_notified_at)})` : ''} span={8} />
          <Box l="Officer" v={officer ? officerLine(officer.rank, officer.name) : t.deputy_name} span={4} />
          <Box l="Date" v={fmtD(t.towed_at)} span={2} />
          <Box l="Time" v={fmtT(t.towed_at)} span={2} />
        </div>
      </section>
      <div className="pf-grid c4">
        <Box l="Vehicle towed from" v={t.location} span={4} />
        <Box l="Vehicle stored at" v={t.stored_at} span={2} />
        <Box l="Name of towing service" v={[t.tow_company, t.tow_driver && `driver ${t.tow_driver}`].filter(Boolean).join(' · ')} />
        <Box l="Telephone no." v={x.towPhone} />
        <Box l="Address" v={x.storageAddress} />
        <Box l="City/state" v={x.storageCity} />
        <Box l="Address" v={x.towAddress} />
        <Box l="City/state" v={x.towCity} />
      </div>
      <section className="pf-sideblock">
        <div className="pf-vlabel">Property identification</div>
        <div className="pf-prop">
          <div className="pf-grid c1">
            <Box l="Reason vehicle towed" v={`${TOW_REASON_LABEL[t.reason as TowReason]}${t.notes ? ` — ${t.notes}` : ''}`} />
            <Box l="Reason for hold status" v={t.hold ? `${t.hold_reason}${t.hold_cleared_at ? ` (hold cleared ${fmtD(t.hold_cleared_at)})` : ''}` : ''} />
            <Box l="Misc. vehicle contents" v={<span className="pf-pre">{t.inventory}</span>} className="tall" />
            {t.damage && <Box l="Damage notes" v={t.damage} />}
          </div>
          <div className="pf-dmgcol">
            <div className="pf-kv3">
              <span>KEYS IN VEHICLE</span>
              {yn(x.keysInVehicle)}
              <span>VEHICLE LOCKED</span>
              {yn(x.locked)}
              <span>TRUNK LOCKED</span>
              {yn(x.trunkLocked)}
            </div>
            <div className="pf-l" style={{ textAlign: 'center' }}>
              Veh. body damage
            </div>
            <DamageDiagram spots={x.damageSpots ?? []} size={150} />
          </div>
        </div>
      </section>
      <div className="pf-grid c3">
        <Box l="Wrecker fee" v={usd(x.wreckerFee)} />
        <Box l="Storage fee" v={usd(x.storageFee)} />
        <Box l="Date released" v={t.released_at ? `${fmtD(t.released_at)}${t.released_to ? ` to ${t.released_to}` : ''}` : ''} />
      </div>
      <p className="pf-pickup">Vehicles are to be picked up at the above location Monday through Friday 8 A.M. to 5 P.M.</p>
      <p className="pf-cert">We, the undersigned, hereby certify the above listed information is correct and the inventory contains all articles within:</p>
      <div className="pf-sigrow4">
        <Line caption={`Signature of tow truck driver${t.driver_signed_name ? ` — ${t.driver_signed_name}` : ''}`} image={driverSignature} wide />
        <Box l="Date" v={fmtD(t.driver_signed_at)} />
        <Box l="Time" v={fmtT(t.driver_signed_at)} />
        <Line caption="Impounding officer" text={officer ? `/s/ ${officer.name}` : ''} />
        <Box l="Employee no." v={officer?.employeeNumber ?? officer?.badge} />
      </div>
    </PrintSheet>
  );
}

/** "Deputy II" + "Deputy Alex Rivera" → "Deputy II Alex Rivera" (the name's own title gives way to the rank). */
const officerLine = (rank: string | null | undefined, name: string) => (rank ? `${rank} ${name.replace(/^(Deputy|Dep\.|Sgt\.?|Sergeant|Cpl\.?|Corporal|Lt\.?|Lieutenant|Capt\.?|Captain|Officer|Ofc\.?)\s+/i, '')}` : name);

export function TicketPrint({ id }: { id: string }) {
  const r = useLoad(() => api.get<any>(`/api/tickets/${id}`), [id]);
  if (!r.data) return r.error ? <ErrorBox error={r.error} /> : <Spinner />;
  const { ticket: t, officer, agency, eticketsApproved } = r.data;
  const p = t.person ?? {};
  const v = t.vehicle ?? {};
  const ow = v.owner?.sameAsDriver ? { first: p.first, middle: p.middle, last: p.last, street: p.street, city: p.city, state: p.state, zip: p.zip } : v.owner ?? {};
  const x = t.extra ?? {};
  const types = new Set<string>(v.types ?? []);
  const viol = t.violations ?? [];
  return (
    <PrintSheet
      label={`Ticket ${t.ticket_number ?? ''}`}
      note={eticketsApproved ? undefined : 'Record copy. Until SCDMV approves e-tickets for your agency, the paper ticket you hand the driver is the summons.'}
    >
      <div className="utt">
        {!eticketsApproved && <div className="pf-stamp utt-stamp">RECORD COPY — NOT A SUMMONS</div>}
        {t.status === 'voided' && <div className="pf-stamp">VOID</div>}
        <div className="utt-head">
          <div className="utt-form">Form S-438</div>
          <div className="utt-title">
            <h1>{t.kind === 'warning' ? 'Written Warning' : 'Uniform Traffic Ticket'}</h1>
            <div>STATE OF SOUTH CAROLINA</div>
            <div>VERSUS</div>
          </div>
          <div className="utt-side">
            <Box l="City/County of" v={x.cityCountyOf} />
            <Box l="Incident no." v={x.incidentNumber ?? t.case_number} mono />
            <Box l="Agency/troop" v={x.agencyTroop ?? agency.name} />
          </div>
        </div>
        <section className="utt-sec">
          <div className="utt-lab">Violator</div>
          <div className="pf-grid c6">
            <Box l="First name" v={p.first} span={2} />
            <Box l="Middle name" v={p.middle} span={2} />
            <Box l="Last name" v={p.last} span={2} />
            <Box l="Street" v={p.street ?? (!p.city ? p.address : '')} span={6} />
            <Box l="City" v={p.city} span={3} />
            <Box l="State" v={p.state} />
            <Box l="Zip code" v={p.zip} span={2} />
            <Box l="DL state" v={p.dlState} />
            <Box l="Driver’s license no." v={p.dl} span={3} mono />
            <Box l="Class" v={p.dlClass} />
            <Box l="CDL" v={yn(p.cdl)} />
            <Box l="Race" v={p.race} />
            <Box l="Sex" v={p.sex} />
            <Box l="Birth date" v={fmtD(p.dob)} />
            <Box l="Hgt." v={p.height} />
            <Box l="Wgt." v={p.weight} />
            <Box l="Hair / eyes" v={[p.hair, p.eyes].filter(Boolean).join(' / ')} />
          </div>
        </section>
        <section className="utt-sec">
          <div className="utt-lab">Vehicle</div>
          <div className="pf-grid c6">
            <Box l="Veh. lic. no." v={v.plate} mono />
            <Box l="State" v={v.plateState} />
            <Box l="Make of veh." v={[v.make, v.model].filter(Boolean).join(' ')} />
            <Box l="Year" v={v.year} />
            <div className="pf-box utt-types" style={{ gridColumn: 'span 2' }}>
              {TICKET_VEHICLE_TYPES.map((y) => (
                <Check key={y.code} on={types.has(y.code)}>
                  {y.label}
                </Check>
              ))}
            </div>
            <Box l="Vehicle owner first name" v={ow.first} span={2} />
            <Box l="Middle name" v={ow.middle} span={2} />
            <Box l="Last name" v={ow.last} span={2} />
            <Box l="Owner street" v={ow.street} span={3} />
            <Box l="City" v={ow.city} />
            <Box l="State" v={ow.state} />
            <Box l="Zip code" v={ow.zip} />
          </div>
        </section>
        {t.kind === 'citation' && (
          <section className="utt-sec">
            <div className="utt-lab">Trial court</div>
            <div>
            <div className="utt-red">YOU ARE SUMMONED TO APPEAR BEFORE THE TRIAL COURT</div>
            <div className="pf-grid c6">
              <Box l="Name of trial court" v={t.court_name} span={3} />
              <Box l="Street" v={x.courtStreet} span={3} />
              <Box l="Date of trial" v={fmtD(t.court_date)} />
              <Box l="Time of trial" v={x.courtTime ? new Date(`2000-01-01T${x.courtTime}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''} />
              <Box l="City" v={x.courtCity} span={2} />
              <Box l="State" v={x.courtState} />
              <Box l="Zip code" v={x.courtZip} />
            </div>
            </div>
          </section>
        )}
        <section className="utt-sec">
          <div className="utt-lab">Violation</div>
          <div className="pf-grid c6">
            <Box l="Violation section no." v={viol.map((y: any) => y.statute).filter(Boolean).join(', ')} span={2} mono />
            <Box
              l={
                <>
                  Violation — court appearance required {yn(x.appearanceRequired)}
                </>
              }
              v={viol.map((y: any) => `${y.description}${y.speed ? ` (${y.speed} in a ${y.posted ?? '?'})` : ''}`).join('; ')}
              span={4}
            />
            <Box l="Date of violation" v={fmtD(t.issued_at)} />
            <Box l="Time of viol." v={fmtT(t.issued_at)} />
            <Box l="SC points" v={x.points} />
            <Box l="B.A. level" v={x.baLevel} />
            <Box l="County" v={x.county} span={2} />
            <Box l="Violation location" v={t.location} span={6} />
            <Box l="Lat" v={t.lat != null ? Number(t.lat).toFixed(5) : ''} span={2} mono />
            <Box l="Long" v={t.lng != null ? Number(t.lng).toFixed(5) : ''} span={2} mono />
            <Box l="City" v={x.city} span={2} />
          </div>
        </section>
        <section className="utt-sec">
          <div className="utt-lab">Officer</div>
          <div className="pf-grid c6">
            <Box l="Name and rank of arresting officer" v={officerLine(x.officerRank ?? officer?.rank, officer?.name ?? t.deputy_name)} span={4} />
            <Box l="SCCJA officer number" v={x.sccja ?? officer?.sccja} span={2} mono />
          </div>
        </section>
        <section className="utt-sec">
          <div className="utt-lab">Arrest</div>
          <div className="pf-grid c6">
            <Box l="Bail deposited" v={usd(x.bailDeposited)} span={2} />
            <Box l="Date of arrest" v={fmtD(x.arrestDate)} span={2} />
            <Box l="Bond amount requested" v={usd(x.bondRequested)} span={2} />
          </div>
        </section>
        {t.kind === 'citation' && (
          <div className="utt-legal">
            <div className="utt-red">PRESENT THIS SUMMONS TO THE TRIAL COURT SHOWN ABOVE</div>
            <p>
              BE SURE YOU UNDERSTAND FROM THE ARRESTING OFFICER THE EXACT TIME AND BEFORE WHOM YOU ARE TO APPEAR. IF THIS TICKET IS WRITTEN FOR A TRAFFIC VIOLATION AND YOU FORFEIT BAIL, PLEAD GUILTY OR NOLO CONTENDERE, OR ARE CONVICTED AFTER A TRIAL, THIS VIOLATION WILL BE PLACED AGAINST YOUR DRIVING RECORD, OR FORWARDED TO YOUR HOME STATE. POINTS FOR
              OUT OF STATE VIOLATOR WILL BE ASSESSED BY YOUR HOME STATE LICENSING AUTHORITY AND MAY DIFFER FROM STATE TO STATE. FAILURE TO COMPLY WITH THE TERMS OF THIS SUMMONS MAY RESULT IN THE SUSPENSION OF YOUR DRIVER’S LICENSE BY YOUR HOME STATE. YOU ARE REQUIRED BY LAW TO APPEAR IN COURT FOR CERTAIN OFFENSES.
            </p>
          </div>
        )}
        <div className="utt-foot">
          <span>{eticketsApproved ? 'VIOLATOR’S COPY' : 'AGENCY RECORD COPY'}</span>
          <span>
            TICKET# <b className="utt-num">{t.ticket_number ?? '—'}</b>
          </span>
        </div>
      </div>
    </PrintSheet>
  );
}
